// src/pages/admin/RoleMapping.tsx - COMPLETE FIX
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Stack,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import RefreshIcon from '@mui/icons-material/Refresh';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx'; 
import { adminRefreshToken } from '../../features/admin/adminApi.ts';

import {
  useListRoleMappingsQuery,
  useCreateRoleMappingMutation,
  useUpdateRoleMappingMutation,
  useDeleteRoleMappingMutation,
  RoleMapping,
  Permission,
  MENU_ITEMS,
} from '../../features/admin/roleMapping.ts';
import { useListRolesQuery } from '../../features/admin/roleMasterApi.ts';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const RoleMappingManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<RoleMapping | null>(null);
  const [formData, setFormData] = useState({
    role_id: '',
    admin_type: '' as 'super_admin' | 'admin' | 'operation' | '',
    module_or_screen: '',
    rights: '',
    permissions: [] as Permission[],
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [authError, setAuthError] = useState<string>('');
  const [isManualRefresh, setIsManualRefresh] = useState(false);

  // Debug: Check current authentication status
  useEffect(() => {
    const checkAuthStatus = () => {
      console.log('🔍 AUTH STATUS CHECK:');
      
      // Check token
      const token = localStorage.getItem('admin_token');
      console.log('🔑 Admin Token:', token ? 'Present' : 'Missing');
      
      // Check role
      const userRole = localStorage.getItem('user_role');
      const adminType = localStorage.getItem('admin_type');
      console.log('👤 User Role:', userRole);
      console.log('👑 Admin Type:', adminType);
      
      // Determine if super admin
      const role = userRole || adminType || '';
      const cleanRole = role.toString().toLowerCase().trim();
      const isSuper = cleanRole.includes('super') || 
                      cleanRole === 'super_admin' || 
                      cleanRole === 'super admin' ||
                      cleanRole === 'superadmin';
      
      console.log('🎯 Is Super Admin:', isSuper);
      setIsSuperAdmin(isSuper);
      
      // Check if we have email/password for token refresh
      const email = localStorage.getItem('admin_email') || localStorage.getItem('user_email');
      const password = localStorage.getItem('admin_password');
      console.log('📧 Email for refresh:', email ? 'Present' : 'Missing');
      console.log('🔐 Password for refresh:', password ? 'Present' : 'Missing');
      
      if (!token) {
        setAuthError('Authentication token missing. Please login again.');
      } else if (!isSuper) {
        setAuthError('Only Super Admin can access this page.');
      } else {
        setAuthError('');
      }
    };
    
    checkAuthStatus();
  }, []);

  // RTK Query hooks
  const { 
    data: mappingsData, 
    isLoading: isLoadingMappings, 
    refetch: refetchMappings,
    error: mappingsError,
    isError: isMappingsError 
  } = useListRoleMappingsQuery(undefined, {
    skip: !isSuperAdmin,
  });
  
  const { 
    data: rolesData, 
    isLoading: isLoadingRoles,
    refetch: refetchRoles 
  } = useListRolesQuery(undefined, {
    skip: !isSuperAdmin,
  });
  
  const [createMapping, { isLoading: isCreating }] = useCreateRoleMappingMutation();
  const [updateMapping, { isLoading: isUpdating }] = useUpdateRoleMappingMutation();
  const [deleteMapping, { isLoading: isDeleting }] = useDeleteRoleMappingMutation();

  // Handle manual refresh with token check
  const handleManualRefresh = async () => {
    setIsManualRefresh(true);
    try {
      // First check/refresh token
      const token = await adminRefreshToken();
      if (!token) {
        setSnackbar({
          open: true,
          message: 'Unable to refresh authentication. Please login again.',
          severity: 'error',
        });
        return;
      }
      
      // Then refetch data
      await Promise.all([refetchMappings(), refetchRoles()]);
      
      setSnackbar({
        open: true,
        message: 'Data refreshed successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Manual refresh error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to refresh data',
        severity: 'error',
      });
    } finally {
      setIsManualRefresh(false);
    }
  };

  // Initialize permissions when admin type changes
  useEffect(() => {
    if (formData.admin_type && !editMode) {
      initializePermissions(formData.admin_type);
    }
  }, [formData.admin_type, editMode]);

  const initializePermissions = (adminType: string) => {
    let defaultPermissions: Permission[] = [];

    if (adminType === 'super_admin') {
      defaultPermissions = MENU_ITEMS.map(item => ({
        menu_id: item.id,
        menu_name: item.name,
        can_read: true,
        can_write: true,
      }));
    } else if (adminType === 'admin') {
      defaultPermissions = MENU_ITEMS.map(item => ({
        menu_id: item.id,
        menu_name: item.name,
        can_read: item.id !== 'admin_users',
        can_write: item.id !== 'admin_users' && item.id !== 'role_master' && item.id !== 'role_mapping',
      }));
    } else if (adminType === 'operation') {
      defaultPermissions = MENU_ITEMS.map(item => {
        if (item.id === 'order_management') {
          return {
            menu_id: item.id,
            menu_name: item.name,
            can_read: true,
            can_write: true,
          };
        } else if (item.id === 'product_management') {
          return {
            menu_id: item.id,
            menu_name: item.name,
            can_read: true,
            can_write: false,
          };
        } else if (item.id === 'dashboard') {
          return {
            menu_id: item.id,
            menu_name: item.name,
            can_read: true,
            can_write: false,
          };
        } else {
          return {
            menu_id: item.id,
            menu_name: item.name,
            can_read: false,
            can_write: false,
          };
        }
      });
    }

    setFormData(prev => ({ ...prev, permissions: defaultPermissions }));
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenDialog = (mapping?: RoleMapping) => {
    if (mapping) {
      setEditMode(true);
      setSelectedMapping(mapping);
      
      const rights = mapping.rights ? mapping.rights.split(',').map(r => r.trim()) : [];
      const modules = mapping.module_or_screen ? mapping.module_or_screen.split(',').map(m => m.trim()) : [];
      
      const permissions: Permission[] = MENU_ITEMS.map(item => {
        const hasAccess = modules.includes('all') || modules.includes(item.id);
        const canRead = hasAccess && (rights.includes('view') || rights.includes('read'));
        const canWrite = hasAccess && (rights.includes('add') || rights.includes('edit') || rights.includes('delete') || rights.includes('write'));
        
        return {
          menu_id: item.id,
          menu_name: item.name,
          can_read: canRead,
          can_write: canWrite,
        };
      });
      
      setFormData({
        role_id: mapping.role_id?.toString() || '',
        admin_type: mapping.admin_type || 'operation',
        module_or_screen: mapping.module_or_screen || '',
        rights: mapping.rights || '',
        permissions: permissions,
      });
    } else {
      setEditMode(false);
      setSelectedMapping(null);
      setFormData({
        role_id: '',
        admin_type: '',
        module_or_screen: '',
        rights: '',
        permissions: [],
      });
    }
    setOpenDialog(true);
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedMapping(null);
    setFormData({
      role_id: '',
      admin_type: '',
      module_or_screen: '',
      rights: '',
      permissions: [],
    });
    setFormErrors({});
  };

  const handleRoleChange = (e: SelectChangeEvent) => {
    setFormData(prev => ({ ...prev, role_id: e.target.value }));
    if (formErrors.role_id) {
      setFormErrors(prev => ({ ...prev, role_id: '' }));
    }
  };

  const handleAdminTypeChange = (e: SelectChangeEvent) => {
    const newType = e.target.value as 'super_admin' | 'admin' | 'operation';
    setFormData(prev => ({ ...prev, admin_type: newType }));
    if (formErrors.admin_type) {
      setFormErrors(prev => ({ ...prev, admin_type: '' }));
    }
  };

  const handlePermissionChange = (menuId: string, field: 'can_read' | 'can_write', value: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(perm =>
        perm.menu_id === menuId
          ? { ...perm, [field]: value, ...(field === 'can_read' && !value ? { can_write: false } : {}) }
          : perm
      ),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.role_id) {
      newErrors.role_id = 'Role is required';
    }

    if (!formData.admin_type) {
      newErrors.admin_type = 'Admin type is required';
    }

    if (formData.permissions.length === 0) {
      newErrors.permissions = 'At least one permission must be configured';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const convertPermissionsToApiFormat = () => {
    const enabledPermissions = formData.permissions.filter(p => p.can_read || p.can_write);
    
    let moduleOrScreen = '';
    if (formData.admin_type === 'super_admin') {
      moduleOrScreen = 'all';
    } else {
      const enabledMenus = enabledPermissions
        .filter(p => p.can_read)
        .map(p => p.menu_id)
        .join(',');
      moduleOrScreen = enabledMenus;
    }

    const rightsSet = new Set<string>();
    enabledPermissions.forEach(perm => {
      if (perm.can_read) rightsSet.add('view');
      if (perm.can_write) {
        rightsSet.add('add');
        rightsSet.add('edit');
        rightsSet.add('delete');
      }
    });

    const rightsArray = Array.from(rightsSet);
    if (rightsArray.length === 0) {
      rightsArray.push('view');
    }

    return {
      module_or_screen: moduleOrScreen,
      rights: rightsArray.join(','),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const apiFormat = convertPermissionsToApiFormat();
      
      if (editMode && selectedMapping) {
        const result = await updateMapping({
          recid: selectedMapping.id,
          role_id: parseInt(formData.role_id),
          admin_type: formData.admin_type,
          module_or_screen: apiFormat.module_or_screen,
          rights: apiFormat.rights,
        }).unwrap();

        if (!result.error) {
          setSnackbar({
            open: true,
            message: result.message || 'Role mapping updated successfully',
            severity: 'success',
          });
          
          refetchMappings();
          setTimeout(() => {
            handleCloseDialog();
          }, 1000);
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to update role mapping',
            severity: 'error',
          });
        }
      } else {
        const result = await createMapping({
          role_id: parseInt(formData.role_id),
          admin_type: formData.admin_type,
          module_or_screen: apiFormat.module_or_screen,
          rights: apiFormat.rights,
        }).unwrap();

        if (!result.error) {
          setSnackbar({
            open: true,
            message: result.message || 'Role mapping created successfully',
            severity: 'success',
          });
          
          refetchMappings();
          setTimeout(() => {
            handleCloseDialog();
          }, 1000);
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to create role mapping',
            severity: 'error',
          });
        }
      }
    } catch (error: any) {
      console.error('Role mapping operation error:', error);
      
      let errorMessage = 'Operation failed';
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.error) {
        errorMessage = error.error;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this role mapping?')) {
      try {
        const result = await deleteMapping(id).unwrap();
        
        if (!result.error) {
          setSnackbar({
            open: true,
            message: result.message || 'Role mapping deleted successfully',
            severity: 'success',
          });
          
          refetchMappings();
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to delete role mapping',
            severity: 'error',
          });
        }
      } catch (error: any) {
        console.error('Delete mapping error:', error);
        setSnackbar({
          open: true,
          message: error?.data?.message || 'Failed to delete role mapping',
          severity: 'error',
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return dateString;
    }
  };

  // If not super admin, show access denied
  if (!isSuperAdmin) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
        <Box sx={{ display: 'flex', flex: 1 }}>
          <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
            <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
          </Box>
          
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <AdminHeader onToggleMenu={handleDrawerToggle} />
            
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              p: 3 
            }}>
              <Alert severity="error" sx={{ maxWidth: 500 }}>
                <Typography variant="h6" gutterBottom>
                  ⚠️ Access Denied
                </Typography>
                <Typography>
                  You do not have permission to access Role Mapping Management.
                  <br />
                  <strong>Only Super Admin</strong> can manage role mappings.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, p: 1, bgcolor: '#fef2f2', borderRadius: 1 }}>
                  <strong>Your role:</strong> {localStorage.getItem('admin_type') || localStorage.getItem('user_role') || 'Unknown'}
                  <br />
                  <strong>Required role:</strong> Super Admin
                </Typography>
                <Button 
                  variant="outlined" 
                  sx={{ mt: 2 }}
                  onClick={() => window.history.back()}
                >
                  Go Back
                </Button>
              </Alert>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  const displayMappings = mappingsData?.role_mappings || [];
  const loading = isLoadingMappings || isManualRefresh;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />

          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AssignmentIndIcon sx={{ fontSize: 28, color: '#008060' }} />
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                    Role Mapping Management
                  </Typography>
                  <Chip 
                    label="Super Admin Only" 
                    size="small" 
                    color="warning" 
                    sx={{ fontWeight: 600 }}
                  />
                  {isMappingsError && (
                    <Chip 
                      label="API Error" 
                      size="small" 
                      color="error" 
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleManualRefresh}
                    disabled={isManualRefresh}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    {isManualRefresh ? 'Refreshing...' : 'Refresh'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    disabled={loading}
                    sx={{
                      textTransform: 'none',
                      bgcolor: '#008060',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#006e52' },
                      '&:disabled': { bgcolor: '#e1e3e5', color: '#6d7175' }
                    }}
                  >
                    Create Mapping
                  </Button>
                </Box>
              </Box>

              {/* Error/Alerts */}
              {authError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    Authentication Error
                  </Typography>
                  <Typography variant="body2">
                    {authError}
                    <Button 
                      size="small" 
                      sx={{ ml: 1 }}
                      onClick={() => window.location.href = '/admin/login'}
                    >
                      Login Again
                    </Button>
                  </Typography>
                </Alert>
              )}

              {isMappingsError && !authError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    Failed to load role mappings
                  </Typography>
                  <Typography variant="body2">
                    {(mappingsError as any)?.data?.message || 'Please check your connection and try again.'}
                    <Button 
                      size="small" 
                      sx={{ ml: 1 }}
                      onClick={handleManualRefresh}
                    >
                      Retry
                    </Button>
                  </Typography>
                </Alert>
              )}

              {/* Mappings Table */}
              <Card
                sx={{
                  borderRadius: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  border: '1px solid #e1e3e5',
                  overflow: 'hidden',
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 300,
                      bgcolor: 'white',
                    }}
                  >
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                    <Typography sx={{ ml: 2 }}>Loading role mappings...</Typography>
                  </Box>
                ) : displayMappings.length > 0 ? (
                  <Box sx={{ bgcolor: 'white', overflowX: 'auto' }}>
                    {/* Table Header */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '80px 1fr 120px 1fr 150px 150px 120px',
                        gap: 2,
                        px: 3,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb',
                        minWidth: '1000px',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        S.No
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Role Name
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Admin Type
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Access
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Created Date
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }}
                      >
                        Updated Date
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6d7175',
                          fontWeight: 600,
                          fontSize: '11px',
                          textTransform: 'uppercase',
                          textAlign: 'right',
                        }}
                      >
                        Actions
                      </Typography>
                    </Box>

                    {/* Table Rows */}
                    {displayMappings.map((mapping, index) => (
                      <Box
                        key={mapping.id}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '80px 1fr 120px 1fr 150px 150px 120px',
                          gap: 2,
                          px: 3,
                          py: 2,
                          alignItems: 'center',
                          borderBottom: '1px solid #e1e3e5',
                          '&:hover': { bgcolor: '#f9fafb' },
                          '&:last-child': { borderBottom: 'none' },
                          transition: 'background-color 0.15s ease',
                          minWidth: '1000px',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#6d7175', fontWeight: 500 }}>
                          {index + 1}
                        </Typography>

                        <Box>
                          <Chip
                            label={mapping.role_name || `Role ${mapping.role_id}`}
                            size="small"
                            sx={{
                              bgcolor: mapping.admin_type === 'super_admin' ? '#fff3cd' : 
                                      mapping.admin_type === 'admin' ? '#d1ecf1' : '#d4edda',
                              color: mapping.admin_type === 'super_admin' ? '#856404' : 
                                    mapping.admin_type === 'admin' ? '#0c5460' : '#155724',
                              fontWeight: 600,
                              fontSize: '12px',
                            }}
                          />
                        </Box>

                        <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' }}>
                          {mapping.admin_type || 'N/A'}
                        </Typography>

                        <Box>
                          <Tooltip title={mapping.module_or_screen || 'No modules assigned'}>
                            <Chip
                              label={mapping.module_or_screen === 'all' ? 'Full Access' : 'Custom'}
                              size="small"
                              sx={{
                                bgcolor: mapping.module_or_screen === 'all' ? '#e8f5e9' : '#e3f2fd',
                                color: mapping.module_or_screen === 'all' ? '#2e7d32' : '#1565c0',
                                fontWeight: 500,
                                fontSize: '11px',
                              }}
                            />
                          </Tooltip>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          {formatDate(mapping.createddt || mapping.created_at)}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          {formatDate(mapping.updateddt || mapping.updated_at)}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(mapping)}
                            disabled={isDeleting}
                            sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(mapping.id)}
                            disabled={isDeleting}
                            sx={{ color: '#d32f2f', '&:hover': { bgcolor: '#ffebee' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <AssignmentIndIcon sx={{ fontSize: 64, color: '#c4cdd5', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No role mappings found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {isMappingsError 
                        ? 'Unable to load data from server. Please check your connection.' 
                        : 'Click "Create Mapping" to configure role permissions'}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog()}
                      disabled={loading}
                      sx={{
                        textTransform: 'none',
                        bgcolor: '#008060',
                        fontWeight: 600,
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#006e52' },
                        '&:disabled': { bgcolor: '#e1e3e5', color: '#6d7175' }
                      }}
                    >
                      Create First Mapping
                    </Button>
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : '16px', maxHeight: '90vh' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e1e3e5',
            fontWeight: 600,
            color: '#202223',
          }}
        >
          {editMode ? 'Edit Role Mapping' : 'Create Role Mapping'}
          <IconButton size="small" onClick={handleCloseDialog} disabled={isCreating || isUpdating}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={3}>
              <FormControl fullWidth error={!!formErrors.role_id} required>
                <InputLabel>Select Role</InputLabel>
                <Select
                  value={formData.role_id}
                  onChange={handleRoleChange}
                  label="Select Role"
                  disabled={isCreating || isUpdating || isLoadingRoles}
                >
                  {isLoadingRoles ? (
                    <MenuItem disabled>Loading roles...</MenuItem>
                  ) : rolesData?.roles && rolesData.roles.length > 0 ? (
                    rolesData.roles.map((role) => (
                      <MenuItem key={role.id} value={role.id.toString()}>
                        {role.role_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No roles available</MenuItem>
                  )}
                </Select>
                {formErrors.role_id && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.role_id}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth error={!!formErrors.admin_type} required>
                <InputLabel>Admin Type</InputLabel>
                <Select
                  value={formData.admin_type}
                  onChange={handleAdminTypeChange}
                  label="Admin Type"
                  disabled={isCreating || isUpdating}
                >
                  <MenuItem value="super_admin">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockOpenIcon fontSize="small" />
                      Super Admin (Full Access to All Menus)
                    </Box>
                  </MenuItem>
                  <MenuItem value="admin">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockIcon fontSize="small" />
                      Admin (All except User Creation & Role Management)
                    </Box>
                  </MenuItem>
                  <MenuItem value="operation">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LockIcon fontSize="small" />
                      Operation (Orders + Read-only Products + Dashboard)
                    </Box>
                  </MenuItem>
                </Select>
                {formErrors.admin_type && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.admin_type}
                  </Typography>
                )}
              </FormControl>

              {formData.permissions.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#202223' }}>
                    Menu Permissions Configuration
                  </Typography>
                  <TableContainer
                    component={Paper}
                    sx={{
                      maxHeight: 400,
                      border: '1px solid #e1e3e5',
                      borderRadius: '8px',
                      overflow: 'auto',
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: '#fafbfb', fontWeight: 600, fontSize: '12px', width: '40%' }}>
                            Menu Name
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#fafbfb', fontWeight: 600, fontSize: '12px', width: '30%' }}>
                            Read Access
                          </TableCell>
                          <TableCell align="center" sx={{ bgcolor: '#fafbfb', fontWeight: 600, fontSize: '12px', width: '30%' }}>
                            Write Access
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.permissions.map((perm) => (
                          <TableRow
                            key={perm.menu_id}
                            sx={{
                              '&:hover': { bgcolor: '#f9fafb' },
                              '&:last-child td': { border: 0 },
                            }}
                          >
                            <TableCell sx={{ fontSize: '13px' }}>
                              {perm.menu_name}
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={perm.can_read}
                                onChange={(e) =>
                                  handlePermissionChange(perm.menu_id, 'can_read', e.target.checked)
                                }
                                disabled={isCreating || isUpdating}
                                sx={{
                                  color: '#008060',
                                  '&.Mui-checked': { color: '#008060' },
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={perm.can_write}
                                onChange={(e) =>
                                  handlePermissionChange(perm.menu_id, 'can_write', e.target.checked)
                                }
                                disabled={!perm.can_read || isCreating || isUpdating}
                                sx={{
                                  color: '#008060',
                                  '&.Mui-checked': { color: '#008060' },
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid #e1e3e5' }}>
            <Button
              onClick={handleCloseDialog}
              disabled={isCreating || isUpdating}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: '#202223',
                borderRadius: '8px',
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isCreating || isUpdating}
              sx={{
                textTransform: 'none',
                bgcolor: '#008060',
                fontWeight: 600,
                borderRadius: '8px',
                px: 2.5,
                '&:hover': { bgcolor: '#006e52' },
              }}
            >
              {isCreating || isUpdating ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  {editMode ? 'Updating...' : 'Creating...'}
                </Box>
              ) : editMode ? (
                'Update Mapping'
              ) : (
                'Create Mapping'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleMappingManagement;